import React, { useState } from 'react';
import { Download, Loader2, RefreshCw, Layers } from 'lucide-react';
import { useOtpSaldosOperacion } from '../hooks/useOtpSaldosOperacion';
import { OtpArqueoOperacion, otpMoney } from './OtpArqueoOperacion';
import { OtpCommissionExportDialog } from './OtpCommissionExportDialog';

const TONOS = {
  united: 'border-sky-300 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20',
  vip_evo: 'border-violet-300 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20',
  sin_asignar: 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20',
  otras_carteras: 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40',
  gastos_bancarios: 'border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/20',
};

export const OtpSaldosOperacion = ({ bankState, bankAccounts, bankCycle, bankLoading, bankError, onRefresh }) => {
  const [exporting, setExporting] = useState(null);
  const { cvus, cvuId, selectCvu, data, loading, error, configReady } = useOtpSaldosOperacion({ bankState, bankAccounts, bankCycle });
  const ops = Object.entries(data?.operaciones || {}).filter(([key, v]) => ['united', 'vip_evo'].includes(key) || v.cant_ent || v.cant_sal);
  const busy = loading || bankLoading;
  return (
    <section className="min-w-0 bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 p-4 space-y-3"
      data-testid="otp-saldos-operacion" aria-busy={!!busy}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Layers className="w-4 h-4 shrink-0 text-indigo-500" />
          <h3 data-testid="otp-ops-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">Saldos por operación</h3>
        </div>
        <div className="flex w-full sm:w-auto min-w-0 max-w-full items-center gap-2">
          <select value={cvuId ?? ''} onChange={e => selectCvu(Number(e.target.value))}
            aria-label="CVU de los saldos por operación" disabled={!configReady || !cvus.length}
            data-testid="otp-ops-cvu" className="min-w-0 w-full sm:w-72 max-w-full text-sm px-2 py-1.5 rounded border dark:bg-gray-700 dark:border-gray-600">
            {cvuId == null && <option value="" data-testid="otp-ops-cvu-empty">{busy || !configReady ? 'Cargando cuentas…' : 'Sin cuentas disponibles'}</option>}
            {cvus.map(c => <option key={c.cvu_id} value={c.cvu_id} data-testid={`otp-ops-cvu-option-${c.cvu_id}`}>
              {c.alias || c.cvu} · {c.titular}
            </option>)}
          </select>
          <button onClick={onRefresh} disabled={busy} data-testid="otp-ops-refrescar"
            title="Actualizar saldos" aria-label="Actualizar saldos"
            className="shrink-0 p-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 transition-colors text-white disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {error && <p role="alert" data-testid="otp-ops-error" className="text-sm text-red-700 dark:text-red-300 break-words">{error}. {data ? 'Se conserva la última lectura de este CVU.' : ''}</p>}
      {bankError && <p role="alert" data-testid="otp-ops-bank-error" className="text-sm text-amber-800 dark:text-amber-300 break-words">Banco: {bankError}</p>}
      {!data && (busy || !configReady || cvuId != null && !error) && <p role="status" data-testid="otp-ops-loading" className="text-sm text-gray-500">Actualizando libros…</p>}
      {!data && !busy && configReady && !cvus.length && <p data-testid="otp-ops-no-accounts" className="text-sm text-gray-500">Sin cuentas disponibles.</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {ops.map(([key, op]) => (
          <div key={key} className={`min-w-0 rounded-lg border p-3 ${TONOS[key] || TONOS.otras_carteras}`} data-testid={`otp-ops-${key}`}>
            <div className="flex items-start justify-between gap-2">
              <p data-testid={`otp-ops-${key}-nombre`} className="min-w-0 text-xs font-semibold text-gray-600 dark:text-gray-300 break-words">{op.nombre}</p>
              {['united', 'vip_evo'].includes(key) && <button type="button" disabled={cvuId == null}
                onClick={() => setExporting({ operation: key, name: op.nombre, cvuId })}
                aria-label={`Exportar comisiones de ${op.nombre}`} title="Exportar comisiones por fechas"
                data-testid={`otp-ops-export-${key}`}
                className="shrink-0 rounded p-1 text-gray-600 dark:text-gray-200 hover:bg-white/70 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors">
                <Download className="w-4 h-4" />
              </button>}
            </div>
            <p data-testid={`otp-ops-${key}-neto`} className="mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100 break-words">{otpMoney(op.neto)}</p>
            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 space-y-1">
              {key === 'gastos_bancarios' ? <>
                <p data-testid="otp-ops-cargo-mr">Resumen mensual: {otpMoney(data.cargos_bancarios?.resumen_mensual)}</p>
                <p data-testid="otp-ops-cargo-ma">Servicio API: {otpMoney(data.cargos_bancarios?.servicio_api)}</p>
                <p data-testid="otp-ops-cargos-cantidad">{data.cargos_bancarios?.cantidad} cargos · sin distribuir</p>
              </> : <>
                <p data-testid={`otp-ops-${key}-entradas`}>Entradas: <span className="tabular-nums">{otpMoney(op.entradas)}</span> ({op.cant_ent})</p>
                <p data-testid={`otp-ops-${key}-salidas`}>Salidas: <span className="tabular-nums">{otpMoney(op.salidas)}</span> ({op.cant_sal})</p>
              </>}
            </div>
          </div>
        ))}
      </div>
      {data && !data.movimientos && !error && <p data-testid="otp-ops-empty" className="text-sm text-gray-500">Todavía no hay movimientos en este CVU.</p>}
      {data && <OtpArqueoOperacion data={data} cvuId={cvuId} bankState={bankState} busy={busy} error={error || bankError} />}
      {exporting && <OtpCommissionExportDialog {...exporting} onClose={() => setExporting(null)} />}
    </section>
  );
};

export default OtpSaldosOperacion;